/**
 * Built-in Show Templates
 *
 * Each template defines the RULES and DEFAULTS for a show format.
 * The rendering engine never checks "if show == top10 do X" — it reads
 * from these config objects. Adding a new show format = adding a new entry here.
 */
import type { ShowTemplate, ShowTemplateId } from '../types';

const DEFAULT_COLOR_PALETTE = {
  primary: '#0A1628',
  secondary: '#1A3A6B',
  accent: '#E8C84A',
  background: '#0D1F3C',
  text: '#FFFFFF',
  ticker: '#0A1628',
  tickerText: '#FFFFFF',
  breakingBanner: '#CC0000',
  breakingText: '#FFFFFF',
};

const DEFAULT_HEADLINE_FONT = {
  fontFamily: 'Roboto',
  fontSize: 52,
  fontWeight: 700 as const,
  color: '#FFFFFF',
  letterSpacing: -0.5,
  lineHeight: 1.2,
};

const DEFAULT_SUBHEADLINE_FONT = {
  fontFamily: 'Roboto',
  fontSize: 28,
  fontWeight: 400 as const,
  color: '#CCCCCC',
  letterSpacing: 0,
  lineHeight: 1.4,
};

// ─── Top 10 News ─────────────────────────────────────────────

const TOP_10_NEWS: ShowTemplate = {
  id: 'top-10-news',
  name: 'Top 10 News',
  nameHi: 'टॉप 10 खबरें',
  maxStories: 10,
  allowedLayouts: ['L1', 'L2', 'L3', 'L4', 'L5', 'L6', 'L7', 'L8', 'L10', 'L12'],
  globalOverlay: {
    ticker: true,
    tickerSpeed: 3,
    logo: true,
    topBar: true,
    lowerThird: false,
    storyCounter: true,
    counterPattern: 'खबर नंबर {n}',
    breakingBannerOnBreaking: true,
  },
  rules: {
    maxConsecutiveSameLayout: 1,
    transitionDuration: 1.5,
    shortTransitionDuration: 0.6,
    periodicLogoAnimation: true,
    logoAnimationInterval: 20,
    preferBreakingTransition: true,
    defaultSceneDuration: 6,
    scenesPerStory: 3,
  },
  colorPalette: DEFAULT_COLOR_PALETTE,
  headlineFont: DEFAULT_HEADLINE_FONT,
  subheadlineFont: DEFAULT_SUBHEADLINE_FONT,
  introPreset: 'full-intro',
  outroPreset: 'standard-outro',
  defaultTransition: 'fade',
  midTransition: 'cut',
};

// ─── Top 20 News ─────────────────────────────────────────────

const TOP_20_NEWS: ShowTemplate = {
  ...TOP_10_NEWS,
  id: 'top-20-news',
  name: 'Top 20 News',
  nameHi: 'टॉप 20 खबरें',
  maxStories: 20,
  rules: {
    ...TOP_10_NEWS.rules,
    scenesPerStory: 2,          // fewer scenes per story to keep total duration reasonable
    defaultSceneDuration: 5,
    shortTransitionDuration: 0.4,
  },
  globalOverlay: {
    ...TOP_10_NEWS.globalOverlay,
    counterPattern: 'NEWS #{n:02}',
  },
};

// ─── 20 Fatafat Khabrein ─────────────────────────────────────

const FATAFAT_20: ShowTemplate = {
  ...TOP_10_NEWS,
  id: 'fatafat-20',
  name: '20 Fatafat Khabrein',
  nameHi: '20 फटाफट खबरें',
  maxStories: 20,
  allowedLayouts: ['L1', 'L5', 'L6', 'L12'],   // fast format — limited layouts
  rules: {
    ...TOP_10_NEWS.rules,
    scenesPerStory: 1,          // one scene per story — fast!
    defaultSceneDuration: 4,
    shortTransitionDuration: 0.3,
    maxConsecutiveSameLayout: 2,
  },
  globalOverlay: {
    ...TOP_10_NEWS.globalOverlay,
    counterPattern: 'फटाफट खबर {n}',
    storyCounter: true,
  },
};

// ─── Breaking News Bulletin ───────────────────────────────────

const BREAKING_BULLETIN: ShowTemplate = {
  ...TOP_10_NEWS,
  id: 'breaking-bulletin',
  name: 'Breaking News Bulletin',
  nameHi: 'ब्रेकिंग बुलेटिन',
  maxStories: null,
  allowedLayouts: ['L1', 'L2', 'L3', 'L5', 'L6', 'L7', 'L12'],
  rules: {
    ...TOP_10_NEWS.rules,
    scenesPerStory: 4,
    defaultSceneDuration: 7,
    transitionDuration: 0.8,
  },
  globalOverlay: {
    ...TOP_10_NEWS.globalOverlay,
    storyCounter: false,
    breakingBannerOnBreaking: true,
  },
  colorPalette: {
    ...DEFAULT_COLOR_PALETTE,
    accent: '#FF3333',
    breakingBanner: '#CC0000',
  },
};

// ─── Morning Bulletin ─────────────────────────────────────────

const MORNING_BULLETIN: ShowTemplate = {
  ...TOP_10_NEWS,
  id: 'morning-bulletin',
  name: 'Morning Bulletin',
  nameHi: 'सुबह बुलेटिन',
  maxStories: null,
  rules: {
    ...TOP_10_NEWS.rules,
    scenesPerStory: 3,
    defaultSceneDuration: 6,
  },
  colorPalette: {
    ...DEFAULT_COLOR_PALETTE,
    primary: '#0f1c35',
    accent: '#FFC947',
  },
};

// ─── Evening Bulletin ─────────────────────────────────────────

const EVENING_BULLETIN: ShowTemplate = {
  ...MORNING_BULLETIN,
  id: 'evening-bulletin',
  name: 'Evening Bulletin',
  nameHi: 'शाम बुलेटिन',
  colorPalette: {
    ...DEFAULT_COLOR_PALETTE,
    primary: '#1a0a2e',
    secondary: '#2d1654',
    accent: '#C084FC',
  },
};

// ─── Business Bulletin ────────────────────────────────────────

const BUSINESS_NEWS: ShowTemplate = {
  ...TOP_10_NEWS,
  id: 'business-news',
  name: 'Business Bulletin',
  nameHi: 'बिज़नेस बुलेटिन',
  maxStories: null,
  allowedLayouts: ['L1', 'L2', 'L3', 'L5', 'L6', 'L7', 'L10', 'L12'],
  rules: {
    ...TOP_10_NEWS.rules,
    scenesPerStory: 3,
    defaultSceneDuration: 7,
  },
  globalOverlay: {
    ...TOP_10_NEWS.globalOverlay,
    counterPattern: 'NEWS #{n:02}',
  },
  colorPalette: {
    ...DEFAULT_COLOR_PALETTE,
    primary: '#052e16',
    secondary: '#14532d',
    accent: '#4ADE80',
  },
};

// ─── Sports Bulletin ──────────────────────────────────────────

const SPORTS_BULLETIN: ShowTemplate = {
  ...TOP_10_NEWS,
  id: 'sports-bulletin',
  name: 'Sports Bulletin',
  nameHi: 'स्पोर्ट्स बुलेटिन',
  maxStories: null,
  rules: {
    ...TOP_10_NEWS.rules,
    scenesPerStory: 3,
    defaultSceneDuration: 6,
  },
  colorPalette: {
    ...DEFAULT_COLOR_PALETTE,
    primary: '#0c1a40',
    secondary: '#1e3a8a',
    accent: '#3B82F6',
  },
};

// ─── Custom Show ──────────────────────────────────────────────

const CUSTOM_SHOW: ShowTemplate = {
  ...TOP_10_NEWS,
  id: 'custom-show',
  name: 'Custom Show',
  nameHi: 'कस्टम शो',
  maxStories: null,
  allowedLayouts: ['L1', 'L2', 'L3', 'L4', 'L5', 'L6', 'L7', 'L8', 'L10', 'L12'],
  rules: {
    ...TOP_10_NEWS.rules,
    scenesPerStory: 3,
  },
  globalOverlay: {
    ...TOP_10_NEWS.globalOverlay,
    storyCounter: false,
    counterPattern: 'NEWS #{n}',
  },
};

// ─── Registry ─────────────────────────────────────────────────

const SHOW_TEMPLATES: Record<ShowTemplateId, ShowTemplate> = {
  'top-10-news': TOP_10_NEWS,
  'top-20-news': TOP_20_NEWS,
  'fatafat-20': FATAFAT_20,
  'breaking-bulletin': BREAKING_BULLETIN,
  'morning-bulletin': MORNING_BULLETIN,
  'evening-bulletin': EVENING_BULLETIN,
  'business-news': BUSINESS_NEWS,
  'sports-bulletin': SPORTS_BULLETIN,
  'custom-show': CUSTOM_SHOW,
};

export function getShowTemplate(id: ShowTemplateId): ShowTemplate {
  const template = SHOW_TEMPLATES[id];
  if (!template) throw new Error(`Unknown show template: ${id}`);
  return template;
}

export function getAllShowTemplates(): ShowTemplate[] {
  return Object.values(SHOW_TEMPLATES);
}

export { SHOW_TEMPLATES };
